import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit'
import {
  Matter,
  CasePlaceOrderUpdateFields,
  CaseForJointCaseOrderUpdateFields
} from 'share/domain/case'
import { GetCaseLevelDashboardResponse } from 'share/queries/cases'
import { saveActiveCaseToSessionStorage } from './activecaseSessionStorage'
import { RootState } from 'app/rootReducer'

export interface ActiveCase {
  isActiveCase: boolean
  matter: Matter | null
}

const initialState: ActiveCase = {
  isActiveCase: false,
  matter: null
}

export function getInactiveActiveCase(): ActiveCase {
  const data: ActiveCase = {
    isActiveCase: false,
    matter: null
  }
  return data
}

export function getEmptyActiveCase(): ActiveCase {
  const data: ActiveCase = {
    isActiveCase: true,
    matter: {
      id: 0,
      bccReference: 0,
      caseNumber: '',
      active: false,
      attorneyId: 0,
      attorneyName: '',
      attorneyAvatarUrl: '',
      caseType: '',
      lastVisitedDate: '',
      status: '',
      openDate: '',
      statuses: [],
      isJoint: false,
      debtor1FirstCourseForJointCaseOrders: null,
      debtor1SecondCourseForJointCaseOrders: null,
      debtor1EmailAddress: null,
      debtor1FirstName: null,
      debtor1LastName: null,
      debtor2EmailAddress: null,
      debtor2FirstName: null,
      debtor2LastName: null,
      isAiEnabled: false,
      isChatBotEnabled: false,
      isCompleteAllChecklistUploadedDocuments: false,
      hasLegacyOrder: false,
      clientCode: null,
      organizationalUnitIdKey: null,
      debtor1HasPlacedFirstCourseOrder: false,
      debtor1HasPlacedSecondCourseOrder: false,
      debtor1HasPlacedFirstCourseOrderStatus: 0,
      debtor1HasPlacedSecondCourseOrderStatus: 0,
      debtor1HasPlacedFirstCourseOrderStatusMessage: null,
      debtor1HasPlacedSecondCourseOrderStatusMessage: null,
      debtor1FirstCourseOrderStep: null,
      debtor1SecondCourseOrderStep: null,
      debtor2FirstCourseForJointCaseOrders: null,
      debtor2SecondCourseForJointCaseOrders: null,
      debtor2HasPlacedFirstCourseOrder: false,
      debtor2HasPlacedSecondCourseOrder: false,
      debtor2HasPlacedFirstCourseOrderStatus: 0,
      debtor2HasPlacedSecondCourseOrderStatus: 0,
      debtor2HasPlacedFirstCourseOrderStatusMessage: null,
      debtor2HasPlacedSecondCourseOrderStatusMessage: null,
      debtor2FirstCourseOrderStep: null,
      debtor2SecondCourseOrderStep: null,
      questionnaireDisplayInClientPortalFlag: false,
      questionnaireExpectedCompletionDate: null,
      questionnaireExpectedCompletionMonth: null,
      questionnaireExpectedCompletionDay: null,
      questionnaireExpectedCompletionYear: null,
      questionnaireExpectedCompletionFormatted: null,
      questionnaireInstructions: null,
      questionnaireShownInstructionsFlag: false,
      questionnaireShownTermsAndConditionsFlag: false,
      dashBoardTitle: null,
      dashBoardHeaderDescription: null,
      dashBoardCurrentStatusDescription: null,
      dashBoardShowSubmitDocumentsFlag: null,
      orderItemSourceTypeId: null,
      dashBoardCourseNumber: null,
      firm: {
        id: 0,
        name: '',
        officeId: '',
        emailAddress: '',
        iconUrl: '',
        street: '',
        city: '',
        state: '',
        zip: '',
        phone: '',
        fax: '',
        website: ''
      }
    }
  }
  return data
}

function getCaseDashboardHeaderDescription(
  caseType: string | null,
  status: string,
  caseNumber: string | null
): string {
  let description = caseType ? caseType : ''
  if (
    status.toLocaleLowerCase() == 'pre-filing' ||
    status.toLocaleLowerCase() == 'prefiling'
  ) {
    description += ` | Case Not Filed`
  } else if (caseNumber) {
    description += ` | ${caseNumber}`
  }
  return description
}

export function loadFirstActiveCaseRecordFromResponse(
  resp: GetCaseLevelDashboardResponse
): ActiveCase {
  const newRecord = getEmptyActiveCase()
  if (resp.cases.caseLevelDashboard.case) {
    const rec = resp.cases.caseLevelDashboard.case
    newRecord.isActiveCase = true
    if (newRecord.matter) {
      newRecord.matter.id = rec.id
      newRecord.matter.bccReference = rec.bccReference
      newRecord.matter.caseNumber = rec.caseNumber
      newRecord.matter.active = rec.active
      newRecord.matter.attorneyId = rec.attorneyId
      newRecord.matter.attorneyName = rec.attorneyName
      newRecord.matter.attorneyAvatarUrl = rec.attorneyAvatarUrl
      newRecord.matter.caseType = rec.caseType
      newRecord.matter.lastVisitedDate = rec.lastVisitedDate
      newRecord.matter.status = rec.status
      newRecord.matter.openDate = rec.openDate
      newRecord.matter.statuses = rec.statuses ? [...rec.statuses] : null
      newRecord.matter.isJoint = rec.isJoint
      newRecord.matter.debtor1FirstCourseForJointCaseOrders =
        rec.debtor1FirstCourseForJointCaseOrders
      newRecord.matter.debtor1SecondCourseForJointCaseOrders =
        rec.debtor1SecondCourseForJointCaseOrders
      newRecord.matter.debtor1EmailAddress = rec.debtor1EmailAddress
      newRecord.matter.debtor1FirstName = rec.debtor1FirstName
      newRecord.matter.debtor1LastName = rec.debtor1LastName
      newRecord.matter.debtor2EmailAddress = rec.debtor2EmailAddress
      newRecord.matter.debtor2FirstName = rec.debtor2FirstName
      newRecord.matter.debtor2LastName = rec.debtor2LastName
      newRecord.matter.isAiEnabled = rec.isAiEnabled
      newRecord.matter.isChatBotEnabled = rec.isChatBotEnabled
      newRecord.matter.isCompleteAllChecklistUploadedDocuments =
        rec.isCompleteAllChecklistUploadedDocuments
      newRecord.matter.hasLegacyOrder = rec.hasLegacyOrder
      newRecord.matter.clientCode = rec.clientCode
      newRecord.matter.organizationalUnitIdKey = rec.organizationalUnitIdKey
      newRecord.matter.debtor1HasPlacedFirstCourseOrder =
        rec.debtor1HasPlacedFirstCourseOrder
      newRecord.matter.debtor1HasPlacedSecondCourseOrder =
        rec.debtor1HasPlacedSecondCourseOrder
      newRecord.matter.debtor1HasPlacedFirstCourseOrderStatus =
        rec.debtor1HasPlacedFirstCourseOrderStatus
      newRecord.matter.debtor1HasPlacedSecondCourseOrderStatus =
        rec.debtor1HasPlacedSecondCourseOrderStatus
      newRecord.matter.debtor1HasPlacedFirstCourseOrderStatusMessage =
        rec.debtor1HasPlacedFirstCourseOrderStatusMessage
      newRecord.matter.debtor1HasPlacedSecondCourseOrderStatusMessage =
        rec.debtor1HasPlacedSecondCourseOrderStatusMessage
      newRecord.matter.debtor1FirstCourseOrderStep =
        rec.debtor1FirstCourseOrderStep
      newRecord.matter.debtor1SecondCourseOrderStep =
        rec.debtor1SecondCourseOrderStep
      newRecord.matter.debtor2FirstCourseForJointCaseOrders =
        rec.debtor2FirstCourseForJointCaseOrders
      newRecord.matter.debtor2SecondCourseForJointCaseOrders =
        rec.debtor2SecondCourseForJointCaseOrders
      newRecord.matter.debtor2HasPlacedFirstCourseOrder =
        rec.debtor2HasPlacedFirstCourseOrder
      newRecord.matter.debtor2HasPlacedSecondCourseOrder =
        rec.debtor2HasPlacedSecondCourseOrder
      newRecord.matter.debtor2HasPlacedFirstCourseOrderStatus =
        rec.debtor2HasPlacedFirstCourseOrderStatus
      newRecord.matter.debtor2HasPlacedSecondCourseOrderStatus =
        rec.debtor2HasPlacedSecondCourseOrderStatus
      newRecord.matter.debtor2HasPlacedFirstCourseOrderStatusMessage =
        rec.debtor2HasPlacedFirstCourseOrderStatusMessage
      newRecord.matter.debtor2HasPlacedSecondCourseOrderStatusMessage =
        rec.debtor2HasPlacedSecondCourseOrderStatusMessage
      newRecord.matter.debtor2FirstCourseOrderStep =
        rec.debtor2FirstCourseOrderStep
      newRecord.matter.debtor2SecondCourseOrderStep =
        rec.debtor2SecondCourseOrderStep
      newRecord.matter.questionnaireDisplayInClientPortalFlag =
        rec.questionnaireDisplayInClientPortalFlag
      newRecord.matter.questionnaireExpectedCompletionDate =
        rec.questionnaireExpectedCompletionDate
      newRecord.matter.questionnaireExpectedCompletionMonth =
        rec.questionnaireExpectedCompletionMonth
      newRecord.matter.questionnaireExpectedCompletionDay =
        rec.questionnaireExpectedCompletionDay
      newRecord.matter.questionnaireExpectedCompletionYear =
        rec.questionnaireExpectedCompletionYear
      newRecord.matter.questionnaireExpectedCompletionFormatted =
        rec.questionnaireExpectedCompletionFormatted
      newRecord.matter.questionnaireInstructions = rec.questionnaireInstructions
      newRecord.matter.questionnaireShownInstructionsFlag =
        rec.questionnaireShownInstructionsFlag
      newRecord.matter.questionnaireShownTermsAndConditionsFlag =
        rec.questionnaireShownTermsAndConditionsFlag
      newRecord.matter.firm.id = rec.firm.id
      newRecord.matter.firm.name = rec.firm.name
      newRecord.matter.firm.officeId = rec.firm.officeId
      newRecord.matter.firm.emailAddress = rec.firm.emailAddress
      newRecord.matter.firm.iconUrl = rec.firm.iconUrl
      newRecord.matter.firm.street = rec.firm.street
      newRecord.matter.firm.city = rec.firm.city
      newRecord.matter.firm.state = rec.firm.state
      newRecord.matter.firm.zip = rec.firm.zip
      newRecord.matter.firm.phone = rec.firm.phone
      newRecord.matter.firm.fax = rec.firm.fax
      newRecord.matter.firm.website = rec.firm.website

      // set dashboard fields
      newRecord.matter.dashBoardHeaderDescription =
        getCaseDashboardHeaderDescription(
          newRecord.matter.caseType,
          newRecord.matter.status,
          newRecord.matter.caseNumber
        )
      newRecord.matter.dashBoardShowSubmitDocumentsFlag =
        !resp.cases.caseLevelDashboard.allDocumentCategoriesCompleteFlag

      if (newRecord.matter.statuses) {
        for (let index = 0; index < newRecord.matter.statuses.length; index++) {
          if (newRecord.matter.statuses[index].current) {
            if (
              newRecord.matter.statuses[index].name.toLowerCase() ===
              'pre-filing'
            ) {
              newRecord.matter.dashBoardTitle =
                "We're preparing your case to be filed."
              newRecord.matter.dashBoardCurrentStatusDescription =
                newRecord.matter.statuses[index].description
              newRecord.matter.dashBoardCourseNumber = 'First'
            } else if (
              newRecord.matter.statuses[index].name.toLowerCase() === 'filed'
            ) {
              newRecord.matter.dashBoardTitle =
                'Congrats! Your case has been filed.'
              newRecord.matter.dashBoardCurrentStatusDescription =
                newRecord.matter.statuses[index].description
              newRecord.matter.dashBoardCourseNumber = 'Second'
            } else {
              newRecord.matter.dashBoardTitle =
                'Well done! Your case has been closed.'
              newRecord.matter.dashBoardCurrentStatusDescription =
                newRecord.matter.statuses[index].description
              newRecord.matter.dashBoardCourseNumber = null
              newRecord.matter.dashBoardShowSubmitDocumentsFlag = false
            }
          }
        }
      }
    }
  }

  return newRecord
}

const activeCase = createSlice({
  name: 'activeCase',
  initialState: initialState,
  reducers: {
    setActiveCase(state, { payload }: PayloadAction<ActiveCase>) {
      saveActiveCaseToSessionStorage(payload)
      return payload
    },
    setQuestionnaireShownInstructionsFlag(
      state,
      { payload }: PayloadAction<boolean>
    ) {
      if (state.matter) {
        state.matter.questionnaireShownInstructionsFlag = payload
      }
    },
    setIsCompleteAllChecklistUploadedDocuments(
      state,
      { payload }: PayloadAction<boolean>
    ) {
      if (state.matter) {
        state.matter.isCompleteAllChecklistUploadedDocuments = payload
      }
    },
    setQuestionnaireShownTermsAndConditionsFlag(
      state,
      { payload }: PayloadAction<boolean>
    ) {
      if (state.matter) {
        state.matter.questionnaireShownTermsAndConditionsFlag = payload
      }
    },
    setCaseForJointCaseOrderUpdateFields(
      state,
      { payload }: PayloadAction<CaseForJointCaseOrderUpdateFields>
    ) {
      if (state.matter) {
        if (payload.firstCourseForJointCaseOrders !== null) {
          if (payload.isDebtor1) {
            state.matter.debtor1FirstCourseForJointCaseOrders =
              payload.firstCourseForJointCaseOrders
          } else {
            state.matter.debtor2FirstCourseForJointCaseOrders =
              payload.firstCourseForJointCaseOrders
          }
        }
        if (payload.secondCourseForJointCaseOrders !== null) {
          if (payload.isDebtor1) {
            state.matter.debtor1SecondCourseForJointCaseOrders =
              payload.secondCourseForJointCaseOrders
          } else {
            state.matter.debtor2SecondCourseForJointCaseOrders =
              payload.secondCourseForJointCaseOrders
          }
        }
      }
    },
    setCasePlaceOrderUpdateFields(
      state,
      { payload }: PayloadAction<CasePlaceOrderUpdateFields>
    ) {
      if (state.matter) {
        if (payload.hasPlacedFirstCourseOrder !== null) {
          if (payload.isDebtor1) {
            state.matter.debtor1HasPlacedFirstCourseOrder =
              payload.hasPlacedFirstCourseOrder
          } else {
            state.matter.debtor2HasPlacedFirstCourseOrder =
              payload.hasPlacedFirstCourseOrder
          }
        }

        if (payload.hasPlacedSecondCourseOrder !== null) {
          if (payload.isDebtor1) {
            state.matter.debtor1HasPlacedSecondCourseOrder =
              payload.hasPlacedSecondCourseOrder
          } else {
            state.matter.debtor2HasPlacedSecondCourseOrder =
              payload.hasPlacedSecondCourseOrder
          }
        }

        if (payload.hasPlacedFirstCourseOrderStatus !== null) {
          if (payload.isDebtor1) {
            state.matter.debtor1HasPlacedFirstCourseOrderStatus =
              payload.hasPlacedFirstCourseOrderStatus
          } else {
            state.matter.debtor2HasPlacedFirstCourseOrderStatus =
              payload.hasPlacedFirstCourseOrderStatus
          }
        }

        if (payload.hasPlacedSecondCourseOrderStatus !== null) {
          if (payload.isDebtor1) {
            state.matter.debtor1HasPlacedSecondCourseOrderStatus =
              payload.hasPlacedSecondCourseOrderStatus
          } else {
            state.matter.debtor2HasPlacedSecondCourseOrderStatus =
              payload.hasPlacedSecondCourseOrderStatus
          }
        }

        if (payload.firstCourseOrderStep !== null) {
          if (payload.isDebtor1) {
            state.matter.debtor1FirstCourseOrderStep =
              payload.firstCourseOrderStep
          } else {
            state.matter.debtor2FirstCourseOrderStep =
              payload.firstCourseOrderStep
          }
        }

        if (payload.secondCourseOrderStep !== null) {
          if (payload.isDebtor1) {
            state.matter.debtor1SecondCourseOrderStep =
              payload.secondCourseOrderStep
          } else {
            state.matter.debtor2SecondCourseOrderStep =
              payload.secondCourseOrderStep
          }
        }

        if (payload.orderItemSourceTypeId) {
          state.matter.orderItemSourceTypeId = payload.orderItemSourceTypeId
        }
      }
    }
  }
})

export const {
  setActiveCase,
  setQuestionnaireShownInstructionsFlag,
  setIsCompleteAllChecklistUploadedDocuments,
  setQuestionnaireShownTermsAndConditionsFlag,
  setCaseForJointCaseOrderUpdateFields,
  setCasePlaceOrderUpdateFields
} = activeCase.actions

export const isDebtorType1 = createSelector(
  (state: RootState) => state.activeCase,
  (state: RootState) => state.authentication,
  (activeCase, authentication) => {
    if (!activeCase) {
      return true
    }
    if (!authentication) {
      return true
    }
    if (!authentication.account) {
      return true
    }
    if (!activeCase.matter) {
      return true
    }
    if (!activeCase.matter.debtor1EmailAddress) {
      return true
    }
    if (!activeCase.matter.debtor2EmailAddress) {
      return true
    }
    if (
      activeCase.matter.debtor1EmailAddress ===
      authentication.account.emailAddress
    ) {
      return true
    }
    if (
      activeCase.matter.debtor2EmailAddress ===
      authentication.account.emailAddress
    ) {
      return false
    }
    return true
  }
)

export const isDebtorType2 = createSelector(
  (state: RootState) => state.activeCase,
  (state: RootState) => state.authentication,
  (activeCase, authentication) => {
    if (!activeCase) {
      return false
    }
    if (!authentication) {
      return false
    }
    if (!authentication.account) {
      return false
    }
    if (!activeCase.matter) {
      return false
    }
    if (!activeCase.matter.debtor1EmailAddress) {
      return false
    }
    if (!activeCase.matter.debtor2EmailAddress) {
      return false
    }
    if (
      activeCase.matter.debtor1EmailAddress ===
      authentication.account.emailAddress
    ) {
      return false
    }
    if (
      activeCase.matter.debtor2EmailAddress ===
      authentication.account.emailAddress
    ) {
      return true
    }
    return false
  }
)

export default activeCase.reducer
